export interface JackpotBandElements {
  container: HTMLElement;
  numberSpan: HTMLElement;
  currencySpan: HTMLElement;
}

type JackpotBandElementsGetter = () => JackpotBandElements | null;

const bandGetters = new Map<Element, JackpotBandElementsGetter>();
// Height is part of the key: media queries (e.g. pickRandom min-height) change band height and font size at equal width.
const lastFitSize = new WeakMap<Element, string>();
let sharedObserver: ResizeObserver | null = null;

const getContainerSize = (container: HTMLElement) => `${container.clientWidth}x${container.clientHeight}`;

const resetBand = ({ numberSpan }: JackpotBandElements) => {
  numberSpan.style.fontSize = '';
};

const measureBand = ({ container, numberSpan, currencySpan }: JackpotBandElements): number | null => {
  const initialFontSize = parseFloat(getComputedStyle(numberSpan).fontSize);

  const containerWidth = container.clientWidth;
  const currencyWidth = currencySpan.offsetWidth;
  const availableWidth = containerWidth - currencyWidth - 4;
  const numberWidth = numberSpan.scrollWidth;

  if (numberWidth > availableWidth && availableWidth > 0) {
    const scale = availableWidth / numberWidth;
    return Math.max(initialFontSize * scale, 8);
  }
  return null;
};

const applyBand = ({ numberSpan }: JackpotBandElements, fontSize: number | null) => {
  if (fontSize !== null) {
    numberSpan.style.fontSize = `${fontSize}px`;
  }
};

export const fitJackpotBand = (band: JackpotBandElements) => {
  resetBand(band);
  applyBand(band, measureBand(band));
  lastFitSize.set(band.container, getContainerSize(band.container));
};

// Batched so a rotation costs one forced layout for all bands instead of one per band.
const handleResize = (entries: ResizeObserverEntry[]) => {
  const bands: JackpotBandElements[] = [];
  const sizes: string[] = [];

  for (const entry of entries) {
    const band = bandGetters.get(entry.target)?.();
    if (!band) continue;
    const size = getContainerSize(band.container);
    if (lastFitSize.get(band.container) === size) continue;
    bands.push(band);
    sizes.push(size);
  }

  bands.forEach(resetBand);
  const fontSizes = bands.map(measureBand);
  bands.forEach((band, index) => {
    applyBand(band, fontSizes[index]);
    lastFitSize.set(band.container, sizes[index]);
  });
};

export const observeJackpotBand = (container: HTMLElement, getElements: JackpotBandElementsGetter) => {
  if (!sharedObserver) {
    sharedObserver = new ResizeObserver(handleResize);
  }
  bandGetters.set(container, getElements);
  sharedObserver.observe(container);
};

export const unobserveJackpotBand = (container: HTMLElement) => {
  bandGetters.delete(container);
  sharedObserver?.unobserve(container);
};
